/**
 * 跨应用登录状态同步工具
 * 用于同步PC端和merchant端的登录状态
 */

class AuthSync {
  constructor(store, appName = 'merchant') {
    this.store = store
    this.appName = appName
    this.channelName = 'auth-sync-channel'
    this.sseRetryCount = 0  // SSE重连次数
    this.maxSseRetries = 3  // 最大重连次数
    this.sseRetryTimer = null  // 重连定时器
    this.init()
  }

  init() {
    // 监听localStorage的storage事件（同源不同标签页之间）
    window.addEventListener('storage', this.handleStorageChange.bind(this))
    
    // 使用BroadcastChannel API（同源不同标签页之间，更可靠）
    if (typeof BroadcastChannel !== 'undefined') {
      this.broadcastChannel = new BroadcastChannel(this.channelName)
      this.broadcastChannel.addEventListener('message', this.handleBroadcastMessage.bind(this))
    }

    // 监听来自其他窗口的postMessage（跨域场景，用于跨子域名通信）
    window.addEventListener('message', this.handlePostMessage.bind(this))
    
    // 监听来自同根域名下其他子域名的cookie变化（通过轮询检查）
    this.startCookiePolling()
    
    // 使用SSE连接后端，实时接收登录状态变化
    // 只在有token时才启动SSE连接，避免在登录页面时不必要的连接
    const token = localStorage.getItem('token')
    if (token) {
      // 延迟启动SSE连接，避免阻塞页面加载
      setTimeout(() => {
        this.startSSEConnection()
      }, 500)
    }
  }

  /**
   * 处理localStorage的storage事件
   * 注意：这个事件只在同源的不同标签页之间触发，当前标签页的修改不会触发
   */
  handleStorageChange(event) {
    if (event.key === 'token' || event.key === 'auth-sync') {
      this.syncFromStorage()
    }
  }

  /**
   * 处理BroadcastChannel消息
   */
  handleBroadcastMessage(event) {
    const { type, appName, data } = event.data
    
    // 忽略自己发送的消息
    if (appName === this.appName) {
      return
    }

    console.log(`[${this.appName}] 收到来自 ${appName} 的同步消息:`, type, data)

    if (type === 'login') {
      this.handleLoginSync(data)
    } else if (type === 'logout') {
      this.handleLogoutSync()
    }
  }

  /**
   * 处理postMessage（用于跨域场景）
   */
  handlePostMessage(event) {
    // 只接受来自同源或信任源的消息
    // 这里可以根据实际需求添加origin检查
    if (event.data && event.data.type === 'auth-sync') {
      const { appName, action, data } = event.data
      
      // 忽略自己发送的消息
      if (appName === this.appName) {
        return
      }

      console.log(`[${this.appName}] 收到来自 ${appName} 的postMessage:`, action, data)

      if (action === 'login') {
        this.handleLoginSync(data)
      } else if (action === 'logout') {
        this.handleLogoutSync()
      }
    }
  }

  /**
   * 处理登录同步
   */
  async handleLoginSync(data) {
    const { token, user } = data
    
    if (token && token !== this.store.state.auth.token) {
      console.log(`[${this.appName}] 同步登录状态，token:`, token)
      
      // 更新localStorage
      localStorage.setItem('token', token)
      
      // 更新store
      this.store.commit('auth/SET_TOKEN', token)
      
      // 同步用户信息
      if (user) {
        this.store.commit('auth/SET_USER', user)
      } else {
        // 如果没有用户信息，尝试从API获取
        try {
          await this.store.dispatch('auth/syncUser')
        } catch (error) {
          console.error('同步用户信息失败:', error)
        }
      }
    }
  }

  /**
   * 处理退出同步
   */
  handleLogoutSync() {
    if (this.store.state.auth.isAuthenticated) {
      console.log(`[${this.appName}] 同步退出状态`)
      
      // 清除localStorage
      localStorage.removeItem('token')
      
      // 更新store
      this.store.commit('auth/LOGOUT')
    }
  }

  /**
   * 从localStorage同步状态
   */
  async syncFromStorage() {
    const token = localStorage.getItem('token')
    const currentToken = this.store.state.auth.token

    if (token && token !== currentToken) {
      // token变化了，需要同步
      console.log(`[${this.appName}] 检测到token变化，同步登录状态`)
      this.store.commit('auth/SET_TOKEN', token)
      
      // 尝试同步用户信息
      try {
        await this.store.dispatch('auth/syncUser')
      } catch (error) {
        console.error('同步用户信息失败:', error)
      }
    } else if (!token && currentToken) {
      // token被删除了，需要退出
      console.log(`[${this.appName}] 检测到token被删除，同步退出状态`)
      this.store.commit('auth/LOGOUT')
    }
  }

  /**
   * 发送登录同步消息
   */
  sendLoginSync(token, user) {
    const message = {
      type: 'login',
      appName: this.appName,
      data: { token, user }
    }

    // 将token保存到cookie（用于跨端口同步）
    if (token) {
      this.setCookie('token', token)
    }

    // 启动SSE连接（如果还没有启动）
    if (token && !this.eventSource) {
      // 重置重连计数
      this.sseRetryCount = 0
      // 延迟启动，避免阻塞登录流程
      setTimeout(() => {
        this.startSSEConnection()
      }, 300)
    }

    // 使用BroadcastChannel发送
    if (this.broadcastChannel) {
      this.broadcastChannel.postMessage(message)
    }

    // 使用localStorage触发storage事件（给同源的其他标签页）
    localStorage.setItem('auth-sync', JSON.stringify({
      ...message,
      timestamp: Date.now()
    }))
    // 立即删除，避免占用空间
    setTimeout(() => {
      localStorage.removeItem('auth-sync')
    }, 100)

    // 使用postMessage发送给所有窗口（跨域场景）
    // 注意：这需要知道目标窗口的引用，或者使用window.opener等
    if (window.opener) {
      window.opener.postMessage({
        type: 'auth-sync',
        ...message
      }, '*')
    }

    // 向所有同源的iframe发送消息
    const iframes = document.querySelectorAll('iframe')
    iframes.forEach(iframe => {
      try {
        iframe.contentWindow.postMessage({
          type: 'auth-sync',
          ...message
        }, '*')
      } catch (e) {
        // 忽略跨域错误
      }
    })

    console.log(`[${this.appName}] 发送登录同步消息`)
  }

  /**
   * 发送退出同步消息
   */
  sendLogoutSync() {
    const message = {
      type: 'logout',
      appName: this.appName
    }

    // 删除cookie中的token（用于跨端口同步）
    this.deleteCookie('token')

    // 使用BroadcastChannel发送
    if (this.broadcastChannel) {
      this.broadcastChannel.postMessage(message)
    }

    // 使用localStorage触发storage事件
    localStorage.setItem('auth-sync', JSON.stringify({
      ...message,
      timestamp: Date.now()
    }))
    setTimeout(() => {
      localStorage.removeItem('auth-sync')
    }, 100)

    // 使用postMessage发送
    if (window.opener) {
      window.opener.postMessage({
        type: 'auth-sync',
        ...message
      }, '*')
    }

    const iframes = document.querySelectorAll('iframe')
    iframes.forEach(iframe => {
      try {
        iframe.contentWindow.postMessage({
          type: 'auth-sync',
          ...message
        }, '*')
      } catch (e) {
        // 忽略跨域错误
      }
    })

    console.log(`[${this.appName}] 发送退出同步消息`)
  }

  /**
   * 启动SSE连接，实时接收登录状态变化
   */
  startSSEConnection() {
    // 检查是否有token，如果没有token则不连接SSE
    const token = localStorage.getItem('token')
    if (!token) {
      console.log(`[${this.appName}] 无token，跳过SSE连接`)
      this.sseRetryCount = 0  // 重置重连计数
      return
    }
    
    // 如果已经达到最大重连次数，不再重连
    if (this.sseRetryCount >= this.maxSseRetries) {
      console.log(`[${this.appName}] SSE重连次数已达上限，停止连接`)
      return
    }

    // 获取API基础URL - 使用与request.js相同的逻辑，避免混合内容错误
    let apiBaseUrl = process.env.VUE_APP_API_BASE_URL || process.env.VUE_APP_BASE_API || '/api'
    
    // 如果是绝对URL，检查协议（避免混合内容错误）
    if (typeof window !== 'undefined' && window.location && !apiBaseUrl.startsWith('/')) {
      const currentProtocol = window.location.protocol
      try {
        const envUrl = new URL(apiBaseUrl)
        // 如果当前页面是HTTPS，但环境变量是HTTP，则强制使用HTTPS
        if (currentProtocol === 'https:' && envUrl.protocol === 'http:') {
          envUrl.protocol = 'https:'
          apiBaseUrl = envUrl.toString()
          console.warn(`[AuthSync] 检测到混合内容风险，已将API地址从HTTP改为HTTPS: ${apiBaseUrl}`)
        }
      } catch (e) {
        // URL解析失败，使用相对路径
        apiBaseUrl = '/api'
      }
    }
    
    // EventSource不支持自定义headers，所以将token作为查询参数传递
    const sseUrl = `${apiBaseUrl}/auth/auth-sync/stream?token=${encodeURIComponent(token)}`

    try {
      // 创建EventSource连接
      this.eventSource = new EventSource(sseUrl, {
        withCredentials: true // 发送cookie
      })

      this.eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          
          // 处理错误
          if (data.error) {
            console.error(`[${this.appName}] SSE错误:`, data.error)
            return
          }

          // 处理登录/退出事件
          if (data.type === 'login') {
            const { token, user } = data.data
            if (token && token !== this.store.state.auth.token) {
              console.log(`[${this.appName}] 通过SSE收到登录事件，同步登录状态`)
              this.handleLoginSync({ token, user })
            }
          } else if (data.type === 'logout') {
            console.log(`[${this.appName}] 通过SSE收到退出事件，同步退出状态`)
            this.handleLogoutSync()
          }
        } catch (error) {
          console.error(`[${this.appName}] 解析SSE消息失败:`, error)
        }
      }

      this.eventSource.onerror = (error) => {
        console.error(`[${this.appName}] SSE连接错误:`, error)
        
        // 检查连接状态
        if (this.eventSource.readyState === EventSource.CLOSED) {
          // 检查当前token是否仍然存在
          const currentToken = localStorage.getItem('token')
          if (!currentToken) {
            console.log(`[${this.appName}] 无token，停止SSE重连`)
            this.stopSSEConnection()
            return
          }
          
          // 检查重连次数
          if (this.sseRetryCount >= this.maxSseRetries) {
            console.log(`[${this.appName}] SSE重连次数已达上限(${this.maxSseRetries})，停止重连`)
            // 可能是token已过期，清除重连计数，等待用户重新登录
            this.sseRetryCount = 0
            this.stopSSEConnection()
            return
          }
          
          // 增加重连次数
          this.sseRetryCount++
          console.log(`[${this.appName}] SSE连接已关闭，5秒后尝试重新连接... (${this.sseRetryCount}/${this.maxSseRetries})`)
          
          // 清除之前的重连定时器
          if (this.sseRetryTimer) {
            clearTimeout(this.sseRetryTimer)
          }
          
          // 延迟重连
          this.sseRetryTimer = setTimeout(() => {
            // 再次检查token是否存在
            const token = localStorage.getItem('token')
            if (!token) {
              console.log(`[${this.appName}] 重连时发现token已清除，停止SSE连接`)
              this.sseRetryCount = 0
              this.stopSSEConnection()
              return
            }
            
            this.stopSSEConnection()
            this.startSSEConnection()
          }, 5000)
        }
      }

      this.eventSource.onopen = () => {
        console.log(`[${this.appName}] SSE连接已建立`)
        // 连接成功，重置重连计数
        this.sseRetryCount = 0
      }

      console.log(`[${this.appName}] 已启动SSE连接: ${sseUrl}`)
    } catch (error) {
      console.error(`[${this.appName}] 启动SSE连接失败:`, error)
    }
  }

  /**
   * 停止SSE连接
   */
  stopSSEConnection() {
    // 清除重连定时器
    if (this.sseRetryTimer) {
      clearTimeout(this.sseRetryTimer)
      this.sseRetryTimer = null
    }
    
    if (this.eventSource) {
      this.eventSource.close()
      this.eventSource = null
      console.log(`[${this.appName}] SSE连接已关闭`)
    }
  }

  /**
   * 轮询检查cookie中的token（用于跨子域名同步）
   * 在方案一（单独域名）下，通过设置domain=.zgyjkj.com的cookie可以实现跨子域名共享
   */
  startCookiePolling() {
    this.lastCookieToken = this.getCookie('token')
    
    this.cookiePollTimer = setInterval(() => {
      const currentCookieToken = this.getCookie('token')
      
      // 检查cookie中的token变化（用于跨子域名同步）
      if (currentCookieToken !== this.lastCookieToken) {
        this.lastCookieToken = currentCookieToken
        if (currentCookieToken && currentCookieToken !== this.store.state.auth.token) {
          console.log(`[${this.appName}] 检测到cookie中的token变化（跨子域名同步），同步登录状态`)
          localStorage.setItem('token', currentCookieToken)
          this.store.commit('auth/SET_TOKEN', currentCookieToken)
          this.store.dispatch('auth/syncUser').catch(error => {
            console.error('同步用户信息失败:', error)
          })
        } else if (!currentCookieToken && this.store.state.auth.token) {
          console.log(`[${this.appName}] 检测到cookie中的token被删除（跨子域名同步），同步退出状态`)
          localStorage.removeItem('token')
          this.store.commit('auth/LOGOUT')
        }
      }
    }, 1000) // 每秒检查一次
  }

  /**
   * 停止cookie轮询
   */
  stopCookiePolling() {
    if (this.cookiePollTimer) {
      clearInterval(this.cookiePollTimer)
      this.cookiePollTimer = null
    }
  }

  /**
   * 获取cookie值
   */
  getCookie(name) {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) return parts.pop().split(';').shift()
    return null
  }

  /**
   * 设置cookie
   */
  setCookie(name, value, days = 7) {
    const expires = new Date()
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
    // 注意：在localhost环境下，不同端口之间cookie是隔离的，无法共享
    // 在生产环境，如果两个应用在同一域名下，可以设置domain为根域名
    // 例如：domain=.example.com（注意前面的点）
    let cookieStr = `${name}=${value}; expires=${expires.toUTCString()}; path=/`
    
    // 如果不是localhost，尝试设置domain（需要根据实际域名调整）
    if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      const hostnameParts = window.location.hostname.split('.')
      if (hostnameParts.length >= 2) {
        // 设置根域名，例如：.example.com
        const rootDomain = '.' + hostnameParts.slice(-2).join('.')
        cookieStr += `; domain=${rootDomain}`
      }
    }
    
    document.cookie = cookieStr
  }

  /**
   * 删除cookie
   */
  deleteCookie(name) {
    let cookieStr = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`
    
    // 如果不是localhost，尝试设置domain
    if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
      const hostnameParts = window.location.hostname.split('.')
      if (hostnameParts.length >= 2) {
        const rootDomain = '.' + hostnameParts.slice(-2).join('.')
        cookieStr += `; domain=${rootDomain}`
      }
    }
    
    document.cookie = cookieStr
  }

  /**
   * 销毁监听器
   */
  destroy() {
    window.removeEventListener('storage', this.handleStorageChange.bind(this))
    window.removeEventListener('message', this.handlePostMessage.bind(this))
    
    if (this.broadcastChannel) {
      this.broadcastChannel.removeEventListener('message', this.handleBroadcastMessage.bind(this))
      this.broadcastChannel.close()
    }
    
    this.stopSSEConnection()
    this.stopCookiePolling()
  }
}

export default AuthSync

